import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../../services/api';

// Brand tokens — parent palette: white, dark slate, brand blue accent.
const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentDarker: '#003D99',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  green: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT    = "'DM Sans', sans-serif";

export default function ForgotPassword() {
  const [email, setEmail]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    // The endpoint always returns 200 with a generic message — we surface
    // the same confirmation regardless of whether the email is registered,
    // mirroring the server's enumeration-safe contract.
    try { await forgotPassword(email.trim()); } catch { /* ignore */ }
    setSubmitted(true);
    setSubmitting(false);
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: C.bg,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '64px 16px',
      fontFamily: BODY_FONT,
      color: C.text,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: '36px 32px 32px',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      }}>
        <h1 style={{
          margin: 0,
          fontFamily: HEADING_FONT,
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: '-0.01em',
        }}>
          Reset your password
        </h1>
        <p style={{ margin: '8px 0 24px', color: C.textMuted, fontSize: 15, lineHeight: 1.55 }}>
          Enter the email address you used to sign up and we'll send you a link to choose a new password.
        </p>

        {submitted ? (
          <div role="status" style={{
            padding: '14px 16px',
            background: C.greenSoft,
            border: `1px solid ${C.greenBorder}`,
            borderRadius: 10,
            color: '#065F46',
            fontSize: 14,
            lineHeight: 1.55,
          }}>
            If that email exists you'll receive a reset link shortly. Check your inbox — and your spam folder, just in case.
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor="email" style={{
              display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6,
            }}>
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck="false"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              required
              style={{
                width: '100%',
                padding: '12px 14px',
                fontFamily: BODY_FONT,
                fontSize: 15,
                color: C.text,
                background: C.surface,
                border: `1.5px solid ${C.border}`,
                borderRadius: 10,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />

            <button
              type="submit"
              disabled={submitting || !email.trim()}
              style={{
                marginTop: 18,
                width: '100%',
                padding: '12px 18px',
                background: submitting ? C.accentDark : C.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontFamily: HEADING_FONT,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: '0.01em',
                cursor: submitting ? 'wait' : 'pointer',
                opacity: !email.trim() ? 0.6 : 1,
              }}
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <div style={{ marginTop: 24, fontSize: 14, color: C.textMuted }}>
          <Link to="/parent/login" style={{ color: C.accent, textDecoration: 'none', fontWeight: 600 }}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
